import { TEvent } from "./types";

/**
 * EventPublisher class responsible for publishing events to subscribers
 */
class EventPublisher {
  private static instance: EventPublisher;
  private subscribers: Array<(event: TEvent) => void> = [];

  /**
   * Private constructor to enforce singleton pattern
   */
  private constructor() {}

  /**
   * Get the singleton instance of EventPublisher
   */
  public static getInstance(): EventPublisher {
    if (!EventPublisher.instance) {
      EventPublisher.instance = new EventPublisher();
    }
    return EventPublisher.instance;
  }

  /**
   * Subscribe to events
   * @param callback Function to be called when an event is published
   * @returns Unsubscribe function
   */
  public subscribe(callback: (event: TEvent) => void): () => void {
    this.subscribers.push(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers = this.subscribers.filter(
        (subscriber) => subscriber !== callback
      );
    };
  }

  /**
   * Publish an event to all subscribers
   * @param event The event to publish
   */
  public publish(event: TEvent): void {
    // Add timestamp if not already present
    if (!event.timestamp) {
      event = {
        ...event,
        timestamp: Date.now(),
      };
    }

    // Log event in development environment
    if (process.env.NODE_ENV === "development") {
      console.log("Event published:", event);
    }

    // Notify all subscribers
    this.subscribers.forEach((subscriber) => {
      try {
        subscriber(event);
      } catch (error) {
        console.error("Error in event subscriber:", error);
      }
    });
  }
}

// Export singleton instance
export const eventPublisher = EventPublisher.getInstance();

/**
 * Helper function to publish events
 * @param event The event to publish
 */
export function publishEvent(event: TEvent): void {
  eventPublisher.publish(event);
}
